import LazyVideo from "@/components/LazyVideo";
import React from 'react';
import { Seo } from '../components/Seo';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Leaf, Sparkles, FlaskConical } from 'lucide-react';

const HERO_VIDEO = 'https://cdn.shopify.com/videos/c/o/v/adf47693e8c3472085cb4223d20aca45.webm';

const paragraphs = [
  'רהאם מנסור, בת 35, נשואה ואם לשלושה ילדים, נולדה וגדלה ברהט וכיום מתגוררת במושב פטיש, שם הקימה קליניקה המשלבת בין ידע רפואי, אהבה עמוקה לעולם הקוסמטיקה וחיבור אותנטי לכוחם של צמחי המרפא.',
  'הדרך המקצועית של רהאם יוצאת דופן ומבוססת על שנים של ניסיון מעשי. היא מגיעה מעולם הרפואה, ובמשך כ־10 שנים עבדה בצמוד לרופאים וסייעה במגוון תחומים רפואיים. עבודה זו הקנתה לה ראייה קלינית רחבה, דיוק מקצועי, הבנה עמוקה של מבנה העור והגוף ויכולת אבחון גבוהה — יתרונות שבאים לידי ביטוי בכל טיפול בקליניקה.',
  'לצד עבודתה, רהאם ממשיכה להעמיק את הידע המקצועי שלה וללמוד תחומים נוספים מעולם הרפואה המשלימה, ביניהם רפואה סינית. הגישה הזו מאפשרת לה להתבונן בגוף כמערכת שלמה ולשלב בין טיפול חיצוני לבין איזון פנימי — עיקרון שמוביל את שיטת העבודה שלה.',
  'המסע שלה בעולם הביוטי החל דווקא מתחום השיער. במשך שנים עבדה כספרית וטיפלה במגוון רחב של בעיות שיער — דלילות, נשירה, קרקפת רגישה ושיער פגום. העבודה הקרובה עם נשים והחשיפה לצרכים האמיתיים שלהן הובילו אותה לחקור פתרונות עמוקים יותר ולחפש דרכים טבעיות ויעילות לשיקום השיער והקרקפת.',
  'מתוך הסקרנות והאהבה לטבע, רהאם החלה לחקור את עולם הצמחים ואת השפעתם על העור והשיער. עם הזמן פיתחה גישה טיפולית המשלבת בין חוכמת הצמחים הערביים המסורתית לבין טכנולוגיות קוסמטיות מתקדמות. עבורה, צמחי המרפא אינם טרנד — אלא דרך טיפול המבוססת על ריפוי, איזון והתחדשות.',
  'החיבור לקוסמטיקה הגיע מתוך רצון להעניק פתרון שלם — לא רק לשיער, אלא גם לעור ולבריאות הכללית. רהאם הוכשרה כקוסמטיקאית ומאז היא משלבת בקליניקה בין מכשור חדשני, רוקחות טבעית בהתאמה אישית ואבחון מקצועי, במטרה להגיע לתוצאות נראות לעין תוך שמירה על בריאות העור.',
];

const closingParagraph =
  'רהאם מאמינה שטיפול אמיתי מתחיל בהקשבה — לעור, לשיער ולצרכים הייחודיים של כל אדם. לכן כל תהליך בקליניקה נפתח באבחון מקצועי וממשיך בבניית תכנית טיפול אישית המשלבת בין טיפול בקליניקה להמשך טיפול בבית.';

const gallery = [
  {
    src: 'https://cdn.shopify.com/s/files/1/0977/6965/2535/files/IMG_3773_1.jpg?v=1787006000',
    alt: 'אווירה בקליניקה',
  },
  {
    src: 'https://cdn.shopify.com/s/files/1/0977/6965/2535/files/IMG_4010.jpg?v=1787003294',
    alt: 'תעודת הסמכה מקצועית',
  },
  {
    src: 'https://cdn.shopify.com/s/files/1/0977/6965/2535/files/IMG_9876.png?v=1787003294',
    alt: 'קבלת תעודת הסמכה מקצועית',
  },
  {
    src: 'https://cdn.shopify.com/s/files/1/0977/6965/2535/files/IMG_2303.jpg?v=1787003289',
    alt: 'רגעים מהקליניקה',
  },
];

const pillars = [
  { icon: FlaskConical, title: 'רוקחות אישית', desc: 'כל פורמולה נרקחת ביד במעבדה שלנו אחרי אבחון אישי.' },
  { icon: Leaf, title: 'צמחי מרפא', desc: 'חוכמת הצמחים הערביים המסורתית לצד טכנולוגיה מודרנית.' },
  { icon: Sparkles, title: 'גישה הוליסטית', desc: 'איזון פנימי וחיצוני — הגוף כמערכת אחת שלמה.' },
];

export const About: React.FC = () => {
  return (
    <>
      <Seo
        title="אודות רהאם מנסור | RIHAM קליניקה אסתטית וטבעית"
        description="הסיפור של רהאם מנסור — שילוב של ניסיון רפואי, רוקחות צמחים ערבית מסורתית וטכנולוגיות קוסמטיות מתקדמות בקליניקה במושב פטיש."
        path="/about"
      />
      <div className="bg-[#EAE6D7] text-[#412018] min-h-[100dvh] relative overflow-hidden" dir="rtl">
      <div className="fixed inset-0 opacity-40 bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')] pointer-events-none mix-blend-multiply z-0" />

      {/* HERO */}
      <section className="relative w-full h-[88vh] min-h-[640px] max-h-[920px] overflow-hidden -mt-32">
        <LazyVideo
          src={HERO_VIDEO}
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover"
         aria-hidden="true" />
        <div className="absolute inset-0 bg-[hsl(47_31%_88%/0.5)]" />
        <div
          className="absolute -bottom-px left-0 right-0 h-[55%] pointer-events-none"
          style={{
            background:
              'linear-gradient(to top, hsl(47 31% 88%) 0%, hsl(47 31% 88% / 0.92) 22%, hsl(47 31% 88% / 0.6) 55%, hsl(47 31% 88% / 0) 100%)',
          }}
        />
        <div className="absolute inset-0 opacity-30 bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')] pointer-events-none mix-blend-multiply" />

        <div className="relative h-full flex flex-col items-center justify-center text-center px-6 pt-28 pb-20">
          <motion.span
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="text-[10px] md:text-[11px] uppercase tracking-[0.45em] text-[#67392b] font-bold mb-7"
          >
            הסיפור שלנו
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-[#412018] leading-[0.88] tracking-wide text-[3rem] sm:text-7xl md:text-8xl lg:text-[9rem] xl:text-[11rem]"
          >
            רהאם <span className="text-[#67392b] italic">מנסור</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-8 max-w-2xl text-[#412018]/75 text-base md:text-lg font-light leading-relaxed"
          >
            רוקחת טבעית, קוסמטיקאית ומטפלת — מקום שבו מסורת פוגשת חדשנות.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.7 }}
            className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          >
            <span className="text-[10px] uppercase tracking-[0.4em] text-[#67392b] font-bold">scroll</span>
            <span className="w-px h-12 bg-[#67392b]/40" />
          </motion.div>
        </div>
      </section>

      {/* PULLQUOTE */}
      <section className="relative z-10 container mx-auto px-6 pt-20 md:pt-28 pb-16 md:pb-24">
        <motion.blockquote
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-5xl mx-auto text-center"
        >
          <p className="font-serif italic text-[#412018] leading-[1.05] text-[2rem] sm:text-4xl md:text-5xl lg:text-[3.6rem]">
            “טיפול אמיתי מתחיל ב<span className="text-[#67392b]">הקשבה</span> —
            <br className="hidden md:block" /> לעור, לשיער ולנשמה.”
          </p>
          <div className="mt-8 flex items-center justify-center gap-4">
            <span className="w-10 h-px bg-[#67392b]/50" />
            <span className="text-[10px] uppercase tracking-[0.4em] text-[#67392b] font-bold">רהאם מנסור</span>
            <span className="w-10 h-px bg-[#67392b]/50" />
          </div>
        </motion.blockquote>
      </section>

      {/* INTRO TWO-COLUMN */}
      <section className="relative z-10 container mx-auto px-6 pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5"
          >
            <span className="block text-[10px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-5">
              chapter 01 — origin
            </span>
            <h2 className="font-serif leading-[0.95] tracking-wide text-[#412018] text-4xl md:text-5xl lg:text-[3.4rem]">
              שורשים <span className="italic text-[#67392b]">במדבר</span>, חזון בטיפוח.
            </h2>
            <div className="w-12 h-px bg-[#67392b] mt-7 mb-7" />
            <p className="text-[#412018]/70 text-[15px] md:text-base font-light leading-[1.85]">
              {paragraphs[0]}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="lg:col-span-7 space-y-8"
          >
            {paragraphs.slice(1, 4).map((p, i) => (
              <p key={i} className="text-[#412018]/80 text-[16px] md:text-[17px] font-light leading-[1.95]">
                <span className="float-right ml-3 mt-1 font-serif italic text-[#67392b]/70 text-[3rem] leading-[0.7]">
                  {String(i + 1).padStart(2, '0')}
                </span>
                {p}
              </p>
            ))}
          </motion.div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative z-10 bg-[#412018] text-[#EAE6D7] py-28 md:py-40 overflow-hidden">
        {/* decorative texture + glow */}
        <div className="absolute inset-0 opacity-[0.07] bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')] pointer-events-none" />
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#fbbf25]/10 blur-[140px] pointer-events-none" />
        <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#67392b]/40 blur-[140px] pointer-events-none" />

        <div className="container mx-auto px-6 max-w-7xl relative">
          {/* header — asymmetric editorial */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-20 md:mb-28">
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="lg:col-span-7"
            >
              <div className="flex items-center gap-4 mb-7">
                <span className="w-10 h-px bg-[#fbbf25]" />
                <span className="text-[10px] uppercase tracking-[0.45em] text-[#fbbf25] font-bold">
                  the philosophy
                </span>
              </div>
              <h2 className="font-serif leading-[0.92] tracking-wide text-5xl md:text-6xl lg:text-[5.5rem]">
                שלוש אבני<br />
                <span className="italic text-[#fbbf25]">היסוד</span>
              </h2>
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="lg:col-span-5 text-[#EAE6D7]/65 font-light leading-[1.9] text-[15px] md:text-base lg:pb-4 lg:border-r lg:border-[#fbbf25]/20 lg:pr-6"
            >
              שלושה עקרונות שמובילים כל טיפול בקליניקה — שילוב של מסורת, מדע ואינטואיציה, שנבנה במשך שנים של עבודה והקשבה.
            </motion.p>
          </div>

          {/* pillars — staggered editorial cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#fbbf25]/15 border border-[#fbbf25]/15">
            {pillars.map((p, i) => (
              <motion.article
                key={p.title}
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.9, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                className={`group relative bg-[#412018] p-10 md:p-12 lg:p-14 hover:bg-[#4d2820] transition-colors duration-700 ${
                  i === 1 ? 'md:translate-y-12' : ''
                }`}
              >
                {/* huge numeral — watermark */}
                <span className="absolute top-6 left-6 font-serif italic text-[#fbbf25]/15 text-[7rem] md:text-[9rem] leading-[0.8] pointer-events-none select-none group-hover:text-[#fbbf25]/25 transition-colors duration-700">
                  0{i + 1}
                </span>

                <div className="relative pt-32 md:pt-40">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="w-8 h-px bg-[#fbbf25] group-hover:w-16 transition-all duration-700" />
                    <span className="text-[10px] uppercase tracking-[0.4em] text-[#fbbf25] font-bold">
                      pillar 0{i + 1}
                    </span>
                  </div>

                  <h3 className="font-serif text-3xl md:text-4xl lg:text-[2.8rem] leading-[1.05] mb-6 tracking-wide">
                    {p.title}
                  </h3>

                  <p className="text-[#EAE6D7]/70 font-light leading-[1.85] text-[15px] md:text-base">
                    {p.desc}
                  </p>

                  <div className="mt-10 flex justify-end">
                    <ArrowLeft size={22} className="text-[#fbbf25]/40 group-hover:text-[#fbbf25] group-hover:-translate-x-2 transition-all duration-500" />
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>


      {/* PERSONAL STORY */}
      <section className="relative z-10 container mx-auto px-6 py-24 md:py-32">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-4 lg:sticky lg:top-32"
          >
            <span className="text-[10px] uppercase tracking-[0.4em] text-[#67392b] font-bold block mb-5">
              chapter 02 — personal
            </span>
            <h2 className="font-serif leading-[0.95] tracking-wide text-[#412018] text-4xl md:text-5xl lg:text-[3.2rem]">
              <span className="italic text-[#67392b]">הסיפור</span><br /> האישי
            </h2>
            <div className="w-12 h-px bg-[#67392b] mt-7" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="lg:col-span-8 space-y-6"
          >
            <p className="font-serif italic text-[#67392b] text-4xl md:text-5xl lg:text-6xl leading-[1.15] tracking-wide">
              ההתמודדות האישית הובילה אותי לחקור לעומק — להבין כל מטופלת, ולפתח גישה עדינה אך אפקטיבית גם לעור הרגיש ביותר.
            </p>
            <p className="text-[#412018]/80 text-[16px] md:text-[17px] font-light leading-[1.95]">
              הסיפור האישי שלה מחזק עוד יותר את השליחות המקצועית. רהאם מתמודדת בעצמה עם סבוריאה — מחלת עור מוכרת הגורמת לרגישות, אדמומיות וגירויים. ההתמודדות האישית הובילה אותה לחקור לעומק פתרונות טיפוליים, להבין את תחושת המטופלות ולפתח גישה עדינה אך אפקטיבית גם לעור רגיש ומאתגר.
            </p>
            <p className="text-[#412018]/80 text-[16px] md:text-[17px] font-light leading-[1.95]">
              {paragraphs[4]}
            </p>
            <p className="text-[#412018]/80 text-[16px] md:text-[17px] font-light leading-[1.95]">
              {paragraphs[5]}
            </p>
            <p className="text-[#412018]/80 text-[16px] md:text-[17px] font-light leading-[1.95]">
              {closingParagraph}
            </p>
          </motion.div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="relative z-10 pb-24 md:pb-32">
        <div className="container mx-auto px-6 mb-12 md:mb-16">
          <div className="flex items-end justify-between gap-8 max-w-7xl mx-auto">
            <div>
              <span className="text-[10px] uppercase tracking-[0.4em] text-[#67392b] font-bold block mb-5">
                gallery
              </span>
              <h2 className="font-serif leading-[0.95] tracking-wide text-[#412018] text-4xl md:text-5xl lg:text-[3.6rem]">
                רגעים <span className="italic text-[#67392b]">מהקליניקה</span>
              </h2>
            </div>
            <Link
              to="/services"
              className="hidden md:inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-[#412018] font-bold hover:text-[#67392b] transition-colors group"
            >
              <span>לטיפולים שלנו</span>
              <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {gallery.map(({ src, alt }, i) => {
              const span =
                i === 0 ? 'md:col-span-2 md:row-span-2 aspect-square'
                : i === 1 ? 'md:col-span-2 aspect-[16/10]'
                : 'aspect-[4/5]';
              return (
                <motion.div
                  key={src}
                  initial={{ opacity: 0, y: 30, scale: 0.96 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, margin: '-5% 0px' }}
                  transition={{ duration: 0.8, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className={`relative overflow-hidden bg-[#412018]/10 group ${span}`}
                >
                  <img
                    src={src}
                    alt={alt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1800ms] ease-luxury group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#412018]/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 container mx-auto px-6 pb-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="max-w-4xl mx-auto text-center"
        >
          <p className="font-serif italic text-[#412018] leading-[1.15] text-3xl md:text-4xl lg:text-5xl mb-10">
            להעניק לכל מטופלת ביטחון, בריאות ו<span className="text-[#67392b]">זוהר אמיתי</span>.
          </p>
          <Link
            to="/appointment"
            className="group inline-flex items-center gap-4 bg-[#412018] hover:bg-[#67392b] text-[#fbbf25] px-9 py-5 transition-colors duration-500"
          >
            <span className="text-[11px] font-bold uppercase tracking-[0.3em]">לקביעת תור</span>
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </section>
    </div>
    </>
  );
};
